import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "@/components/ui/select";
import { RESERVE, PAGE_SIZES, ASSETS } from "@/data/content";
import { Reveal } from "@/lib/reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", organisation: "", email: "", phone: "", page_size: "", message: "" };

export const Reserve = () => {
 const [form, setForm] = useState(empty);
 const [loading, setLoading] = useState(false);
 const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

 const submit = async (e) => {
 e.preventDefault();
 if (!form.name || !form.email || !form.page_size) {
 toast.error("Please add your name, email and a page size.");
 return;
 }
 setLoading(true);
 try {
 await axios.post(`${API}/reserve`, form);
 toast.success("Reservation received, check your inbox for confirmation.");
 setForm(empty);
 } catch (err) {
 toast.error("Something went wrong. Please email us directly at gday@carbon8.org.au");
 } finally {
 setLoading(false);
 }
 };

 return (
 <section id="reserve" data-testid="reserve" className="relative bg-paper py-24 md:py-32">
 <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-[1fr_1fr] lg:gap-20">
 {/* Editorial column */}
 <div>
 <Reveal>
 <span className="font-body text-xs uppercase tracking-[0.3em] text-ochre">{RESERVE.kicker}</span>
 <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-ink sm:text-5xl lg:text-6xl">
 {RESERVE.heading}
 </h2>
 </Reveal>
 <Reveal delay={0.06}>
 <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-ink/75">{RESERVE.body}</p>
 </Reveal>
 <Reveal delay={0.1}>
 <figure className="mt-10 max-w-md -rotate-1">
 <div className="tape mx-auto h-6 w-28 rounded-sm" />
 <img
 src={ASSETS.speaker}
 alt="A Carbon8 farmer speaking at a field day"
 className="mt-[-6px] w-full rounded-sm border-8 border-paper object-cover shadow-[0_16px_40px_rgba(42,32,21,0.16)]"
 />
 <figcaption className="mt-3 text-center font-script text-xl text-sage">
 “Nearly everyone we interviewed raised the same worry…”
 </figcaption>
 </figure>
 </Reveal>
 </div>

 {/* Form */}
 <Reveal delay={0.08}>
 <form
 onSubmit={submit}
 data-testid="reserve-form"
 className="rounded-xl border border-clay bg-cream p-6 md:p-9"
 >
 <div className="grid gap-5 sm:grid-cols-2">
 <Field label="Name *" id="name">
 <Input data-testid="reserve-name" value={form.name} onChange={set("name")} placeholder="Jane Farmer" className="bg-paper" />
 </Field>
 <Field label="Organisation" id="org">
 <Input data-testid="reserve-org" value={form.organisation} onChange={set("organisation")} placeholder="Farm / business" className="bg-paper" />
 </Field>
 <Field label="Email *" id="email">
 <Input data-testid="reserve-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="bg-paper" />
 </Field>
 <Field label="Phone" id="phone">
 <Input data-testid="reserve-phone" value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className="bg-paper" />
 </Field>
 </div>

 <div className="mt-5">
 <Label className="font-body text-sm text-ink/70">Page size *</Label>
 <Select value={form.page_size} onValueChange={set("page_size")}>
 <SelectTrigger data-testid="reserve-page-size" className="mt-1.5 bg-paper">
 <SelectValue placeholder="Choose a page size" />
 </SelectTrigger>
 <SelectContent>
 {PAGE_SIZES.map((p) => (
 <SelectItem key={p} value={p} data-testid={`page-size-${p}`}>{p}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="mt-5">
 <Label className="font-body text-sm text-ink/70">Message</Label>
 <Textarea
 data-testid="reserve-message"
 value={form.message}
 onChange={set("message")}
 placeholder="Tell us a little about you and how you'd like to be part of the 2026 edition."
 rows={4}
 className="mt-1.5 bg-paper"
 />
 </div>

 <button
 type="submit"
 disabled={loading}
 data-testid="reserve-submit"
 className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-7 py-3.5 font-body text-sm font-medium text-cream transition-all duration-300 hover:bg-ink disabled:opacity-60"
 >
 {loading ? <Loader2 size={17} className="animate-spin" /> : <>Reserve my page <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></>}
 </button>
 <p className="mt-4 text-center font-body text-xs text-ink/50">
 No payment now, enquiries are confirmed personally by the Carbon8 team.
 </p>
 </form>
 </Reveal>
 </div>
 </section>
 );
};

const Field = ({ label, id, children }) => (
 <div>
 <Label htmlFor={id} className="font-body text-sm text-ink/70">{label}</Label>
 <div className="mt-1.5">{children}</div>
 </div>
);
